import { useState, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import './ownerKit.css'

export type OwnerEmptyScaffoldProps = {
  title?: string
  body?: string
  icon?: ReactNode
  explanation?: string
  configLabel?: string
  configPath?: string
  onConfigClick?: () => void
  docsUrl?: string
  howDataGetsHere?: [string, string, string]
  filtersMock?: string[]
  className?: string
}

const DEFAULT_BULLETS: [string, string, string] = [
  'Events and user submissions populate this surface in real-time.',
  'Config policies and thresholds determine what appears in this feed.',
  'Operations runbooks can trigger background synching on demand.',
]

export function OwnerEmptyScaffold({
  title,
  body,
  icon,
  explanation,
  configLabel,
  onConfigClick,
  docsUrl = '/docs',
  howDataGetsHere = DEFAULT_BULLETS,
  filtersMock = ['All time', 'Status: any', 'Filter…'],
  className = '',
}: OwnerEmptyScaffoldProps) {
  const { t } = useNls()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={`ownerEmptyScaffold ${className}`} data-testid="owner-empty-scaffold">
      <div className="ownerEmptyTopBar">
        <div className="ownerEmptyFilterBar" aria-hidden="true">
          {filtersMock.map((filter, i) => (
            <span key={i} className="ownerEmptyFilterMock">
              {filter}
            </span>
          ))}
        </div>
        <span className="ownerEmptyHeaderCount" aria-label={t('NLSOwnerConsoleAriaZeroItems')}>
          {t('NLSOwnerConsoleZeroItems')}
        </span>
      </div>

      <div className="ownerEmptyCenterBlock">
        {icon && <div className="ownerEmptyIcon">{icon}</div>}
        {title && <strong className="ownerEmptyTitle" style={{ display: 'block', fontSize: 16, fontWeight: 600, color: 'var(--text-high)', marginBottom: 4 }}>{title}</strong>}
        {body && <p className="ownerEmptyBody" style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text-mid)' }}>{body}</p>}
        {explanation && <p className="ownerEmptyExplanation">{explanation}</p>}
        {configLabel && (
          <button
            type="button"
            className="ownerEmptyConfigLink"
            onClick={onConfigClick}
          >
            {configLabel} →
          </button>
        )}
      </div>

      <div className="ownerEmptyCollapsible">
        <div
          className="ownerEmptyCollapsibleSummary"
          onClick={() => setExpanded(!expanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setExpanded(!expanded)
            }
          }}
          aria-expanded={expanded}
        >
          <span>{t('NLSOwnerConsoleHowDataGetsHere')}</span>
          <span>{expanded ? '▲' : '▼'}</span>
        </div>
        {expanded && (
          <div className="ownerEmptyCollapsibleBody">
            <ul>
              {howDataGetsHere.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
            <a
              href={docsUrl}
              target="_blank"
              rel="noreferrer"
              className="ownerEmptyConfigLink"
              style={{ paddingLeft: 0 }}
            >
              {t('NLSOwnerConsoleReadArchitectureDocs')}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
